import React, { useEffect, useState } from "react";
import { Table, Tag, Space, Input, Typography, Spin } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  saasControlService,
  InternalUserWithSessionInfo,
} from "~/services/saas-control";
import { Role } from "~/utils/constants";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Text } = Typography;

export const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<InternalUserWithSessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await saasControlService.getInternalUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "red";
      case Role.ADMIN:
        return "blue";
      case Role.LEAD:
        return "orange";
      case Role.STAFF:
        return "green";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "Super Admin";
      case Role.ADMIN:
        return "Admin";
      case Role.LEAD:
        return "Lead";
      case Role.STAFF:
        return "Staff";
      default:
        return role;
    }
  };

  const columns: ColumnsType<InternalUserWithSessionInfo> = [
    {
      title: "Team Member",
      key: "user",
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>
              {record.firstName && record.lastName
                ? `${record.firstName} ${record.lastName}`
                : record.username}
            </div>
            <Text type="secondary" className="text-xs">
              {record.email || "No email"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
      filters: [
        { text: "Super Admin", value: Role.SUPER_ADMIN },
        { text: "Admin", value: Role.ADMIN },
        { text: "Lead", value: Role.LEAD },
        { text: "Staff", value: Role.STAFF },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: "Sessions",
      dataIndex: "sessionCount",
      key: "sessionCount",
      render: (count) => <span className="font-medium">{count}</span>,
      sorter: (a, b) => a.sessionCount - b.sessionCount,
    },
    {
      title: "Last Login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      render: (date) =>
        date ? (
          <div>
            <div>{dayjs(date).format("MMM D, YYYY")}</div>
            <Text type="secondary" className="text-xs">
              {dayjs(date).fromNow()}
            </Text>
          </div>
        ) : (
          <Text type="secondary">Never</Text>
        ),
      sorter: (a, b) => {
        if (!a.lastLoginAt) return 1;
        if (!b.lastLoginAt) return -1;
        return (
          new Date(a.lastLoginAt).getTime() - new Date(b.lastLoginAt).getTime()
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isCurrentlyActive",
      key: "isCurrentlyActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? "Online" : "Offline"}
        </Tag>
      ),
      filters: [
        { text: "Online", value: true },
        { text: "Offline", value: false },
      ],
      onFilter: (value, record) => record.isCurrentlyActive === value,
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("MMM D, YYYY"),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
  ];

  const filteredUsers = users.filter((user) => {
    const searchLower = search.toLowerCase();
    return (
      user.username.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <Input
          placeholder="Search team members..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} team members`,
        }}
      />
    </div>
  );
};
